import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
  GridToolbarQuickFilter,
  GridToolbar,
} from "@mui/x-data-grid";

import { useTheme } from "@mui/material";
import { tokens } from "../theme";
import { Form, Alert } from "react-bootstrap";
import { apiUrl } from "../services/api";
import {
  Delete,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import Header from "../components/Header";
import { format } from "date-fns";

const Etat = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [etatToDelete, setEtatToDelete] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [etats, setEtats] = useState([]);
  const [voitures, setVoitures] = useState([]);
  const [formData, setFormData] = useState({
    id_voiture: "",
    etat: "",
    description: "",
    date_retour: "",
  });
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [availableVoitures, setAvailableVoitures] = useState([]);
  const [currentEditingVoiture, setCurrentEditingVoiture] = useState(null);

  const fetchEtats = async () => {
    try {
      const response = await axios.get(`${apiUrl}/etat`);
      const transformedEtats = response.data.map((etat) => ({
        ...etat,
        date_retour: etat.date_retour ? formatDate(etat.date_retour) : "",
      }));
      setEtats(transformedEtats);
    } catch (err) {
      // Set etats to an empty array in case of error
      setEtats([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return format(date, "yyyy-MM-dd");
  };

  const fetchVoitures = async () => {
    try {
      const response = await axios.get(`${apiUrl}/voiture`);
      //console.log("Voitures API response:", response.data);
      setVoitures(response.data);
    } catch (err) {
      console.error("Error fetching voitures:", err);
    }
  };

  const updateAvailableVoitures = (
    currentEtats,
    currentVoitures = voitures,
    editingVoitureId = null
  ) => {
    // console.log("Current Etats:", currentEtats);
    // console.log("Current Voitures:", currentVoitures);

    if (currentEtats.length === 0) {
      // console.log("No etats, all vehicles are available");
      setAvailableVoitures(currentVoitures);
      return;
    }

    // Create a Set of all id_voiture values in currentEtats for quick lookup
    const etatsVoitureIds = new Set(
      currentEtats.map((etat) => etat.id_voiture)
    );

    const latestEtats = currentEtats.reduce((acc, etat) => {
      if (
        !acc[etat.id_voiture] ||
        new Date(etat.date_retour) > new Date(acc[etat.id_voiture].date_retour)
      ) {
        acc[etat.id_voiture] = etat;
      }
      return acc;
    }, {});

    const availableVoituresList = currentVoitures.filter((v) => {
      // Check if the id_voiture exists in currentEtats
      if (etatsVoitureIds.has(v.id_voiture)) {
        return false; // If it doesn't exist in currentEtats, it's not available
      }

      const latestEtat = latestEtats[v.id_voiture];
      return (
        !latestEtat ||
        latestEtat.etat === "disponible" ||
        v.id_voiture === editingVoitureId
      );
    });

    // console.log("Available Voitures List:", availableVoituresList);
    setAvailableVoitures(availableVoituresList);
  };

  useEffect(() => {
    fetchEtats();
    fetchVoitures();
  }, []);

  useEffect(() => {
    updateAvailableVoitures(etats, voitures);
  }, [voitures, etats]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === "etat" && value === "disponible") {
      setFormData((prev) => ({
        ...prev,
        date_retour: "",
      }));
    }
  };

  const handleAddEtat = async (e) => {
    e.preventDefault();

    try {
      let dataToSend = { ...formData };

      // Remove date_retour if etat is "disponible"
      if (dataToSend.etat === "disponible") {
        delete dataToSend.date_retour;
      }

      let response;
      if (editing) {
        response = await axios.put(`${apiUrl}/etat/${editId}`, dataToSend);
      } else {
        response = await axios.post(`${apiUrl}/etat`, dataToSend);
      }
      //console.log("Server response:", response.data);
      await fetchEtats();
      setEditing(false);
      setEditId(null);
      setCurrentEditingVoiture(null);
      setFormData({
        id_voiture: "",
        etat: "",
        description: "",
        date_retour: "",
      });
      setMessage(
        editing ? "Etat updated successfully" : "Etat added successfully"
      );
      setMessageType("success");
    } catch (err) {
      console.error("Error adding/updating etat:", err);
      setError(err.response?.data?.message || "Failed to add/update etat");
      setMessage("Failed to add/update etat");
      setMessageType("error");
    }
    setShowForm(false);
  };

  const handleEditEtat = (etat) => {
    setEditing(true);
    setEditId(etat.id_etat);
    setCurrentEditingVoiture(etat.id_voiture);
    setFormData({
      id_voiture: etat.id_voiture,
      etat: etat.etat,
      description: etat.description,
      date_retour: formatDate(etat.date_retour),
    });
    updateAvailableVoitures(etats, voitures, etat.id_voiture);
    setShowForm(true);
  };

  const handleOpenConfirmDialog = (id) => {
    setEtatToDelete(id);
    setConfirmOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmOpen(false);
    setEtatToDelete(null);
  };

  const handleDeleteEtat = async () => {
    if (!etatToDelete) return;

    try {
      await axios.delete(`${apiUrl}/etat/${etatToDelete}`);
      const updatedEtats = etats.filter((e) => e.id_etat !== etatToDelete);
      setEtats(updatedEtats);
      updateAvailableVoitures(updatedEtats);
      setMessage("Etat deleted successfully");
      setMessageType("success");
    } catch (err) {
      console.error("Error deleting etat:", err);
      setMessage("Failed to delete etat");
      setMessageType("error");
    }
    setConfirmOpen(false);
  };

  const columns = [
    {
      field: "id_etat",
      headerName: "ID",
      width: 70,
      cellClassName: "name-column--cell2",
    },
    {
      field: "id_voiture",
      headerName: "Voiture",
      width: 150,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <span>
          {voitures.find((v) => v.id_voiture === params.value)?.matricule ||
            "-"}
        </span>
      ),
    },
    {
      field: "etat",
      headerName: "Etat",
      width: 120,
      cellClassName: "name-column--cell2",
    },
    {
      field: "description",
      headerName: "Description",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "date_retour",
      headerName: "Date de retour",
      width: 150,
      cellClassName: "name-column--cell",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      cellClassName: "actions",
      renderCell: (params) => (
        <>
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEditEtat(params.row)}
            color="inherit"
          />
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => handleOpenConfirmDialog(params.row.id_etat)}
            color="inherit"
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <Header title="Etats" subtitle="Gestion des états des voitures." />
      {error && <Alert variant="danger">{error}</Alert>}
      {message && (
        <div
          className={`message ${messageType}`}
          style={{
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: messageType === "success" ? "#d4edda" : "#f8d7da",
            color: messageType === "success" ? "#155724" : "#721c24",
            border: `1px solid ${
              messageType === "success" ? "#c3e6cb" : "#f5c6cb"
            }`,
          }}
        >
          {message}
        </div>
      )}
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir supprimer cet état ?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteEtat} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Button
        onClick={() => setShowForm(!showForm)}
        variant="contained"
        sx={{
          color: colors.primary[500],
          backgroundColor: colors.greenAccent[500],
          ":hover": {
            color: colors.primary[500],
            backgroundColor: colors.greenAccent[300],
          },
          marginBottom: "20px",
        }}
      >
        {showForm ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </Button>
      {showForm && (
        <Form onSubmit={handleAddEtat}>
          <Box
            display="grid"
            gap="20px"
            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            width={"90wv"}
          >
            <FormControl
              fullWidth
              sx={{ gridColumn: "span 1" }}
              color="secondary"
              margin="normal"
              required
            >
              <InputLabel id="voiture-label">Voiture</InputLabel>
              <Select
                labelId="voiture-label"
                id="id_voiture"
                value={formData.id_voiture}
                label="Voiture"
                onChange={handleInputChange}
                name="id_voiture"
              >
                {availableVoitures.length > 0 ? (
                  availableVoitures.map((option) => (
                    <MenuItem key={option.id_voiture} value={option.id_voiture}>
                      {option.marque + " - " + option.matricule}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No available vehicles</MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              sx={{ gridColumn: "span 1" }}
              color="secondary"
              margin="normal"
              required
            >
              <InputLabel id="etat-label">Etat</InputLabel>
              <Select
                labelId="etat-label"
                id="etat"
                value={formData.etat}
                label="Etat"
                onChange={handleInputChange}
                name="etat"
              >
                <MenuItem value="disponible">Disponible</MenuItem>
                <MenuItem value="enpanne">En panne</MenuItem>
                <MenuItem value="vendu">Vendu</MenuItem>
                <MenuItem value="autre">Autre</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />

            {formData.etat !== "disponible" && (
              <TextField
                label="Date de retour"
                name="date_retour"
                type="date"
                value={formData.date_retour}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          </Box>
          <Button
            variant="contained"
            type="submit"
            sx={{
              color: colors.primary[500],
              backgroundColor: colors.greenAccent[500],
              ":hover": {
                color: colors.primary[500],
                backgroundColor: colors.greenAccent[300],
              },
            }}
          >
            {editing ? "Modifier Etat" : "Ajouter Etat"}
          </Button>
        </Form>
      )}

      <Box
        m="40px 0 0 0"
        maxHeight="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .name-column--cell2": {
            color: colors.redAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "& .MuiDataGrid-root .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-main": {
            overflow: "auto",
          },
        }}
      >
        <DataGrid
          rows={etats}
          columns={columns}
          getRowId={(row) => row.id_etat}
          components={{ Toolbar: CustomToolbar }}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableExtendRowFullWidth={true}
          autoHeight={true}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </div>
  );
};

const csvOptions = { fileName: "Etats", delimiter: ";", utf8WithBom: true };
const printOptions = {
  hideFooter: true,
  hideToolbar: true,
  includeCheckboxes: true,
};

function CustomExportButtons(props) {
  return (
    <GridToolbarExportContainer {...props}>
      <GridCsvExportMenuItem options={csvOptions} />
      <GridPrintExportMenuItem options={printOptions} />
    </GridToolbarExportContainer>
  );
}

function CustomToolbar(props) {
  return (
    <GridToolbarContainer {...props}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <CustomExportButtons />
      <Box sx={{ marginLeft: "auto", pl: 0, pr: 0, pb: 0, pt: 0 }}>
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
}

export default Etat;
